import {
  Component, Input, Output, EventEmitter, ElementRef, ViewChild,
  OnChanges, OnDestroy, SimpleChanges, ChangeDetectorRef,
} from '@angular/core';
import { ReceiptTemplate, TemplateElement } from '../../models/receipt-template.model';
import { MergeTag, CanvasDataMap, SAMPLE_DATA } from '../../models/merge-tag.model';

const MM_TO_PX  = 3.7795; // at 96 DPI
const GRID_MM   = 5;      // snap grid size in mm — matches the visual grid overlay

export interface TagDropEvent {
  tag: MergeTag;
  /** x position in mm from the left edge of the canvas */
  x: number;
  /** y position in mm from the top edge of the canvas */
  y: number;
}

@Component({
  selector: 'app-template-canvas',
  templateUrl: './template-canvas.component.html',
  styleUrls: ['./template-canvas.component.scss'],
})
export class TemplateCanvasComponent implements OnChanges, OnDestroy {

  @ViewChild('canvasEl') canvasElRef!: ElementRef<HTMLDivElement>;

  @Input() template!: ReceiptTemplate;
  /** Array of currently selected elements (may be empty, one, or many). */
  @Input() selectedElements: TemplateElement[] = [];
  @Input() previewMode = false;
  @Input() zoom = 100;
  @Input() showGrid = false;
  /** When set, canvas elements render real job-card data instead of SAMPLE_DATA. */
  @Input() customData: CanvasDataMap | null = null;

  /** Emits when a single element is clicked; includes shiftKey for multi-select. */
  @Output() elementSelected       = new EventEmitter<{ element: TemplateElement | null; shiftKey: boolean }>();
  /** Emits for all elements in a group drag during live movement (no history push). */
  @Output() groupMoved            = new EventEmitter<TemplateElement[]>();
  /** Emits once when a group drag ends (mouseup) — triggers history push. */
  @Output() groupMoveEnded        = new EventEmitter<TemplateElement[]>();
  /** Emits the set of elements captured by a rubber-band selection. */
  @Output() multiSelected         = new EventEmitter<TemplateElement[]>();
  @Output() elementMoved          = new EventEmitter<TemplateElement>();
  @Output() elementMoveEnded      = new EventEmitter<TemplateElement>();
  @Output() elementRemoved        = new EventEmitter<TemplateElement>();
  @Output() elementContentChanged = new EventEmitter<TemplateElement>();
  @Output() tagDropped            = new EventEmitter<TagDropEvent>();

  public isDragOver = false;

  // ─── Rubber-band selection state ──────────────────────────────────────────
  rubberBand: { startX: number; startY: number; currentX: number; currentY: number } | null = null;

  /**
   * Set to true by startGroupDrag() just before the click event that follows
   * mouseup fires.  Cleared in selectElement() so that click-after-group-drag
   * does not accidentally reset the multi-selection.
   */
  private justGroupDragged = false;

  /**
   * Measured overflow for each table element (element.id → overflow in mm).
   * Populated after a DOM measurement pass; drives the Y-offset logic.
   * An entry of 0 means "fits within configured height — no push needed".
   */
  private tableOverflowsMm = new Map<string, number>();
  private measurePending = false;

  constructor(private cdr: ChangeDetectorRef) {}

  ngOnChanges(changes: SimpleChanges): void {
    // Schedule a measurement pass whenever anything that affects auto-height
    // rendering changes.  text elements are always auto-height so a pass is
    // always needed; tables and customer_notes only grow when data is active.
    if (changes['customData'] || changes['previewMode'] || changes['template'] || changes['zoom']) {
      this.scheduleMeasurement();
    }
  }

  ngOnDestroy(): void {
    this.tableOverflowsMm.clear();
  }

  /**
   * Schedules a single DOM-measurement pass for the next event-loop tick.
   * The guard flag prevents stacking multiple setTimeout calls if inputs
   * change rapidly (e.g. zoom + previewMode toggled in the same tick).
   */
  scheduleMeasurement(): void {
    if (this.measurePending) return;
    this.measurePending = true;
    setTimeout(() => {
      this.measurePending = false;
      this.measureTableOverflows();
    }, 0);
  }

  /**
   * Synchronous measurement pass — called by TemplateEditorComponent via
   * @ViewChild immediately before capturing outerHTML for print.  Unlike
   * scheduleMeasurement() this does NOT defer to a setTimeout, so the
   * caller can rely on the DOM top-positions being correct the instant
   * this method returns.
   */
  forceRemeasure(): void {
    this.measurePending = false; // cancel any pending async pass
    this.measureTableOverflows();
    this.cdr.detectChanges();
  }

  /**
   * Queries the rendered DOM for every auto-height element (tables with live
   * data, free-text elements, and customer_notes fields), reads its actual
   * getBoundingClientRect().height, and computes how many mm it overflows its
   * configured height.  If any value changed, calls detectChanges() so
   * sortedElements re-runs with the real offsets.
   *
   * Elements that are NOT currently auto-height have their stale entries
   * removed from the map so they no longer contribute to Y-offsets.
   */
  private measureTableOverflows(): void {
    const canvasEl = this.canvasElRef?.nativeElement;
    if (!canvasEl) return;

    const hasData = this.customData != null || this.previewMode;
    let changed = false;

    for (const el of this.template.elements) {
      // Determine whether this element is currently rendered with height:auto.
      // Must match the isAutoHeight condition in canvas-element.component.ts exactly.
      const isAutoHeight =
        el.elementType === 'text' ||
        el.tagKey === 'customer_notes' ||
        el.tagKey === 'address' ||
        (el.elementType === 'table'         && hasData) ||
        (el.elementType === 'barcode-table' && hasData) ||
        (el.elementType === 'image'         && hasData);

      if (!isAutoHeight) {
        // Clear any stale measurement so it no longer drives a Y-offset.
        if (this.tableOverflowsMm.has(el.id)) {
          this.tableOverflowsMm.delete(el.id);
          changed = true;
        }
        continue;
      }

      const domEl = canvasEl.querySelector(
        `[data-element-id="${el.id}"]`,
      ) as HTMLElement | null;
      if (!domEl) continue;

      const actualHeightPx     = domEl.getBoundingClientRect().height;
      const configuredHeightPx = el.height * this.scale;
      const overflowPx = Math.max(0, actualHeightPx - configuredHeightPx);
      const overflowMm = overflowPx / this.scale;

      const prev = this.tableOverflowsMm.get(el.id) ?? -1;
      // Use a small tolerance (0.3 mm) to avoid infinite micro-corrections.
      if (Math.abs(prev - overflowMm) > 0.3) {
        this.tableOverflowsMm.set(el.id, overflowMm);
        changed = true;
      }
    }

    if (changed) {
      // Trigger a fresh render so sortedElements picks up the new offsets.
      this.cdr.detectChanges();
    }
  }

  // ─── Dimensions ───────────────────────────────────────────────────────────
  get canvasWidthPx(): number {
    return this.template.widthMm * MM_TO_PX * (this.zoom / 100);
  }

  get canvasHeightPx(): number {
    return this.template.heightMm * MM_TO_PX * (this.zoom / 100);
  }

  get scale(): number {
    return MM_TO_PX * (this.zoom / 100);
  }

  // ─── Drop from merge-tags panel (native HTML5 drag-and-drop) ────────────
  onDragOver(event: DragEvent): void {
    if (this.previewMode) return;
    // Allow drop by preventing default; set visual cue.
    event.preventDefault();
    this.isDragOver = true;
  }

  onDragLeave(): void {
    this.isDragOver = false;
  }

  onNativeDrop(event: DragEvent): void {
    event.preventDefault();
    this.isDragOver = false;
    if (this.previewMode) return;

    const raw = event.dataTransfer?.getData('application/merge-tag');
    if (!raw) return;

    let tag: MergeTag;
    try { tag = JSON.parse(raw); } catch { return; }

    const canvasEl = this.canvasElRef?.nativeElement;
    if (canvasEl) {
      const rect    = canvasEl.getBoundingClientRect();
      const xPx     = event.clientX - rect.left;
      const yPx     = event.clientY - rect.top;
      const rawXMm  = xPx / this.scale;
      const rawYMm  = yPx / this.scale;
      const snapX   = this.showGrid ? Math.round(rawXMm / GRID_MM) * GRID_MM : rawXMm;
      const snapY   = this.showGrid ? Math.round(rawYMm / GRID_MM) * GRID_MM : rawYMm;
      const xMm     = Math.max(0, Math.min(snapX, this.template.widthMm  - 10));
      const yMm     = Math.max(0, Math.min(snapY, this.template.heightMm - 5));
      this.tagDropped.emit({ tag, x: +xMm.toFixed(2), y: +yMm.toFixed(2) });
    } else {
      this.tagDropped.emit({ tag, x: 10, y: 10 });
    }
  }

  // ─── Selection ────────────────────────────────────────────────────────────
  selectElement(element: TemplateElement, event: MouseEvent): void {
    event.stopPropagation();
    // Suppress the click that follows a group-drag mouseup.
    if (this.justGroupDragged) { this.justGroupDragged = false; return; }
    this.elementSelected.emit({ element, shiftKey: event.shiftKey });
  }

  deselectAll(): void {
    this.elementSelected.emit({ element: null, shiftKey: false });
  }

  isSelected(element: TemplateElement): boolean {
    return this.selectedElements.some(e => e.id === element.id);
  }

  // ─── Canvas mousedown — routes to group drag or rubber-band ───────────────
  /**
   * Fired on the canvas div before any canvas-element's own mousedown.
   * Uses event bubbling: canvas-elements fire their own handlers first, but
   * since (mousedown) on the canvas div fires as the event bubbles up to it,
   * we use this to intercept group-drag starts and rubber-band starts.
   */
  onCanvasMouseDown(event: MouseEvent): void {
    if (this.previewMode) return;

    const ceEl = (event.target as HTMLElement).closest('[data-element-id]') as HTMLElement | null;

    if (ceEl) {
      // A canvas element was clicked — start group drag if it's in the selection.
      if (this.selectedElements.length > 1) {
        const id = ceEl.getAttribute('data-element-id');
        if (this.selectedElements.some(e => e.id === id)) {
          this.startGroupDrag(event);
        }
      }
    } else {
      // Empty canvas area — start rubber-band selection.
      this.startRubberBand(event);
    }
  }

  // ─── Group drag ───────────────────────────────────────────────────────────
  private startGroupDrag(startEvent: MouseEvent): void {
    startEvent.preventDefault();

    const startPositions = this.selectedElements.map(el => ({ id: el.id, x: el.x, y: el.y }));
    const startMx = startEvent.clientX;
    const startMy = startEvent.clientY;

    const computeUpdated = (clientX: number, clientY: number): TemplateElement[] => {
      const rawDx = (clientX - startMx) / this.scale;
      const rawDy = (clientY - startMy) / this.scale;
      // Snap the delta uniformly so relative positions are preserved.
      const dx = this.showGrid ? Math.round(rawDx / GRID_MM) * GRID_MM : rawDx;
      const dy = this.showGrid ? Math.round(rawDy / GRID_MM) * GRID_MM : rawDy;
      return this.selectedElements.map(el => {
        const start = startPositions.find(s => s.id === el.id)!;
        const newX = Math.max(0, Math.min(start.x + dx, this.template.widthMm  - el.width));
        const newY = Math.max(0, Math.min(start.y + dy, this.template.heightMm - el.height));
        return { ...el, x: +newX.toFixed(2), y: +newY.toFixed(2) };
      });
    };

    const onMove = (mv: MouseEvent) => {
      this.groupMoved.emit(computeUpdated(mv.clientX, mv.clientY));
    };

    const onUp = (upEvent: MouseEvent) => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup',   onUp);
      // Flag so the click event that fires right after mouseup is ignored.
      this.justGroupDragged = true;
      this.groupMoveEnded.emit(computeUpdated(upEvent.clientX, upEvent.clientY));
    };

    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup',   onUp);
  }

  // ─── Rubber-band selection ────────────────────────────────────────────────
  /** CSS style object for the rubber-band rectangle overlay. Null when below threshold. */
  get rubberBandStyle(): Record<string, string> | null {
    if (!this.rubberBand) return null;
    const { startX, startY, currentX, currentY } = this.rubberBand;
    const left   = Math.min(startX, currentX);
    const top    = Math.min(startY, currentY);
    const width  = Math.abs(currentX - startX);
    const height = Math.abs(currentY - startY);
    if (width < 5 && height < 5) return null;
    return { left: `${left}px`, top: `${top}px`, width: `${width}px`, height: `${height}px` };
  }

  private startRubberBand(startEvent: MouseEvent): void {
    const canvasEl = this.canvasElRef?.nativeElement;
    if (!canvasEl) return;
    startEvent.preventDefault();

    const canvasRect = canvasEl.getBoundingClientRect();
    const startX = startEvent.clientX - canvasRect.left;
    const startY = startEvent.clientY - canvasRect.top;
    this.rubberBand = { startX, startY, currentX: startX, currentY: startY };

    const onMove = (mv: MouseEvent) => {
      this.rubberBand = {
        startX, startY,
        currentX: mv.clientX - canvasRect.left,
        currentY: mv.clientY - canvasRect.top,
      };
      this.cdr.detectChanges();
    };

    const onUp = () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup',   onUp);

      const rb = this.rubberBandStyle;
      if (rb) {
        // Suppress the canvas click that fires after mouseup so deselectAll() doesn't clear.
        const suppressClick = (e: Event) => {
          e.stopPropagation();
          document.removeEventListener('click', suppressClick, true);
        };
        document.addEventListener('click', suppressClick, true);

        // Convert rubber-band px rect to mm and find intersecting elements.
        const left   = parseFloat(rb['left'])   / this.scale;
        const top    = parseFloat(rb['top'])    / this.scale;
        const right  = (parseFloat(rb['left']) + parseFloat(rb['width']))  / this.scale;
        const bottom = (parseFloat(rb['top'])  + parseFloat(rb['height'])) / this.scale;
        const selected = this.template.elements.filter(el =>
          el.x < right && (el.x + el.width)  > left &&
          el.y < bottom && (el.y + el.height) > top,
        );
        if (selected.length > 0) {
          this.multiSelected.emit(selected);
        }
      }
      // If rb is null (tiny/no movement), the click fires normally → deselectAll().

      this.rubberBand = null;
      this.cdr.detectChanges();
    };

    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup',   onUp);
  }

  // ─── Sorted elements with dynamic Y offsets ───────────────────────────────
  /**
   * Returns elements sorted by zIndex, ready for rendering.
   *
   * When in preview / live-data mode, table elements use `height: auto` so
   * they grow to show all rows.  This getter shifts every element whose top
   * edge is at or past a table's configured bottom edge downward by the exact
   * overflow measured from the DOM (populated by measureTableOverflows()).
   *
   * On the first render (before measurement runs) elements sit at their
   * original positions; after the first setTimeout(0) cycle they jump to the
   * correct positions — typically imperceptible to the user.
   *
   * The stored template is never mutated — only the rendered coordinates change.
   */
  get sortedElements(): TemplateElement[] {
    const elements = [...this.template.elements];

    // No measured overflows yet → return original layout.
    if (this.tableOverflowsMm.size === 0) {
      return elements.sort((a, b) => a.zIndex - b.zIndex);
    }

    // Build list of all auto-height elements that overflow their configured box.
    // Covers tables (live data), free-text elements, and customer_notes fields.
    const overflows: Array<{ configuredBottomMm: number; overflowMm: number }> = [];
    for (const el of elements) {
      const overflowMm = this.tableOverflowsMm.get(el.id) ?? 0;
      if (overflowMm > 0) {
        overflows.push({ configuredBottomMm: el.y + el.height, overflowMm });
      }
    }

    if (overflows.length === 0) return elements.sort((a, b) => a.zIndex - b.zIndex);

    // For each element, accumulate overflow from every table whose configured
    // bottom edge is at or above this element's top edge.
    const adjusted = elements.map(el => {
      const extraMm = overflows
        .filter(t => t.configuredBottomMm <= el.y)
        .reduce((sum, t) => sum + t.overflowMm, 0);
      return extraMm > 0 ? { ...el, y: +(el.y + extraMm).toFixed(2) } : el;
    });

    return adjusted.sort((a, b) => a.zIndex - b.zIndex);
  }

  /**
   * Returns the original (pre-push-down) Y of the element as stored in the
   * template — used to populate data-original-y on each canvas-element so
   * the print window script can reset and recalculate positions in the print
   * rendering context.
   */
  getOriginalY(id: string): number {
    return this.template.elements.find(e => e.id === id)?.y ?? 0;
  }

  trackById(_: number, el: TemplateElement): string {
    return el.id;
  }
}
